import { useUser } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { type NavigateFunction, useLocation } from "react-router";
import type {
  ArgusConnectionContextValue,
  PendingQuestion,
} from "../../contexts/argus-connection-context";
import { appendArgusParams } from "../../lib/argus/argus-params";
import { buildDefaultArgusContext } from "../../lib/argus/argus-session-context";
import { getArgusUserInfoFromClerk } from "../../lib/argus/argus-user";
import { matchRouteContext } from "../../lib/argus/route-registry";
import { useUserPreferences } from "../../providers/user-preferences-provider";
import type { ArgusGatewayEvent, ArgusMessage } from "../../types/argus-events";
import { useArgusGateway } from "./use-argus-gateway";
import { useArgusInstance } from "./use-argus-instance";
import { useArgusInstances } from "./use-argus-instances";
import { useArgusSessionTranscript } from "./use-argus-session-transcript";
import { useArgusSSE } from "./use-argus-sse";
import { useHealArgusInstance } from "./use-heal-argus-instance";

// ---------------------------------------------------------------------------
// Event buffer reducer
// ---------------------------------------------------------------------------

type EventAction =
  | { type: "add"; event: ArgusGatewayEvent }
  | { type: "reset" }
  | { type: "seed"; messages: ArgusMessage[] };

interface EventState {
  events: ArgusGatewayEvent[];
  seededMessages: ArgusMessage[];
}

const INITIAL_STATE: EventState = { events: [], seededMessages: [] };

function eventReducer(state: EventState, action: EventAction): EventState {
  switch (action.type) {
    case "add":
      return { ...state, events: [...state.events, action.event] };
    case "reset":
      return INITIAL_STATE;
    case "seed":
      return { events: [], seededMessages: action.messages };
  }
}

// ---------------------------------------------------------------------------
// Context injection
// ---------------------------------------------------------------------------

/**
 * Build a text prefix describing the user's current page. Prepended to
 * messages so the agent has situational awareness when the user sends from
 * the sidebar. Returns empty string for unmatched routes (e.g. /settings).
 */
function buildContextPrefix(pathname: string): string {
  const match = matchRouteContext(pathname);
  if (!match) return "";
  const paramPairs = Object.entries(match.params)
    .map(([key, value]) => `${key}=${value}`)
    .join(", ");
  const paramsBlock = paramPairs ? ` (${paramPairs})` : "";
  return `[Context: ${match.label}${paramsBlock}]\n\n`;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Internal hook that backs `ArgusConnectionProvider`. Owns the SSE
 * connection, the gateway HTTP client, and the live event buffer.
 *
 * Assumes a single instance per org (v1 scope). If the user has no
 * instance, most fields stay null/empty — the /argus page renders a
 * zero-state CTA in that case.
 *
 * Phase 3 additions:
 *   - Reads `useLocation()` so `sendMessage` can prepend route context.
 *   - Initialises / persists `sidebarOpen` via `useUserPreferences`.
 *
 * Phase 4a wires `answerQuestion` + `pendingQuestions` scanning; Phase 5
 * adds related-resources extraction from the event stream.
 */
export const useArgusConnection = (): ArgusConnectionContextValue => {
  const { preferences, updatePreferences } = useUserPreferences();
  const location = useLocation();
  const { user: clerkUser } = useUser();
  // Per-message sender identity — attached to every outbound user message
  // so the agent can tell who's talking in org-shared sessions. `null`
  // when signed out (the gateway treats `user` as optional).
  const argusUser = useMemo(() => getArgusUserInfoFromClerk(clerkUser), [clerkUser]);
  const argusUserRef = useRef(argusUser);
  argusUserRef.current = argusUser;

  // Hydrate sidebar open state from preferences on mount. We intentionally
  // read `preferences.argusSidebarOpen` once via a ref so toggling from
  // multiple consumers doesn't fight the persisted value during a single
  // render cycle.
  const [sidebarOpen, setSidebarOpenState] = useState<boolean>(() =>
    Boolean(preferences.argusSidebarOpen),
  );
  const hydratedRef = useRef(false);
  useEffect(() => {
    // When the user signs into a different org, preferences re-hydrate —
    // mirror that into local state.
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      setSidebarOpenState(Boolean(preferences.argusSidebarOpen));
    }
  }, [preferences.argusSidebarOpen]);

  const setSidebarOpen = useCallback(
    (open: boolean) => {
      setSidebarOpenState(open);
      updatePreferences({ argusSidebarOpen: open });
    },
    [updatePreferences],
  );

  const toggleSidebar = useCallback(() => {
    setSidebarOpenState((prev) => {
      const next = !prev;
      updatePreferences({ argusSidebarOpen: next });
      return next;
    });
  }, [updatePreferences]);

  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [pendingSessionPreview, setPendingSessionPreview] = useState<string | null>(null);
  // User messages shown optimistically as soon as they're sent. Pi-coding-agent
  // only emits assistant/tool events, so without this list the user's own
  // text would stay invisible until the next transcript reload.
  const [optimisticUserMessages, setOptimisticUserMessages] = useState<string[]>([]);
  // True from the moment `sendMessage` is called until the first assistant
  // event arrives for this turn. Drives the ephemeral "Argus is thinking"
  // indicator in the transcript — fills the silent gap between submit and
  // the first piece of streaming content (can be a few seconds).
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  // True while a transcript fetch is in flight on session switch. Drives
  // the "Loading conversation…" indicator in the transcript area.
  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);
  // True when the active session's transcript was served from the S3
  // archive (live gateway unavailable — instance paused, updated, or
  // deleted). Drives the "This session has ended." banner and the
  // disabled composer on the argus page.
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [{ events, seededMessages }, dispatch] = useReducer(eventReducer, INITIAL_STATE);

  // --- Message queue (Phase 6 — backend-managed via pi) ---
  //
  // Queue state comes from `queue_update` SSE events emitted by
  // pi-coding-agent whenever steer()/followUp()/clearQueue() are called.
  // No local queue — the backend is the source of truth.
  const [queuedMessages, setQueuedMessages] = useState<{
    steering: readonly string[];
    followUp: readonly string[];
  }>({ steering: [], followUp: [] });

  // Hydrated session state from the one-shot `GET /session/queue/:id` call
  // we make on session activation. Lets `isAgentWorking` and tool-block
  // hydration know about turns/tools already in flight before the SSE
  // subscription started — without this, switching to an active session
  // mid-turn shows no stop button (because no `turn_start` ever arrives in
  // the live event buffer for this turn).
  const [hydratedStreaming, setHydratedStreaming] = useState<boolean>(false);
  const [hydratedOpenToolCallIds, setHydratedOpenToolCallIds] = useState<readonly string[]>([]);

  // True from the moment `sendMessage` is called until the next `agent_end`
  // observed via SSE. Covers the case where a freshly-started session's
  // initial `agent_start` is fired by pi-coding-agent *before* the FE's
  // SSE subscription attaches — without this flag the stop button never
  // appears for the first turn of a new session, because neither the live
  // event buffer nor the hydrated snapshot catch it reliably in the race
  // window between `startSession` returning and pi's prompt() spinning up.
  //
  // Why `agent_end` and not `turn_end`: pi-agent-core emits `turn_*` events
  // per LLM call, so a multi-tool response fires several `turn_end`s
  // mid-work. `agent_start`/`agent_end` wrap the *entire* prompt() call
  // and match the semantics of `session.isStreaming`.
  const [awaitingAgentEnd, setAwaitingAgentEnd] = useState(false);

  // True after the user clicks Stop, cleared on the next turn_start.
  const [wasStopped, setWasStopped] = useState(false);

  // Single-instance-per-org — Phase 1 scope.
  const { instances } = useArgusInstances({ limit: 1 });
  const instanceId = instances?.[0]?.id;

  const { instance, refetch: refetchInstance } = useArgusInstance(instanceId, {
    refetchInterval: 5_000,
  });

  const hostUrl = instance?.host_url ?? null;
  const accessToken = instance?.access_token ?? null;
  const instanceStatus = instance?.instance_status ?? null;

  const gateway = useArgusGateway(hostUrl, accessToken);

  // --- Phase 4b: navigate bridge ---
  //
  // `<ArgusNavigateBridge />` (mounted inside the dashboard layout — always
  // inside the router tree) pushes the router's navigate function into this
  // ref via `registerNavigate`. The SSE event handler reads the latest
  // value on each `navigate` tool_execution_end.
  const navigateRef = useRef<NavigateFunction | null>(null);
  const registerNavigate = useCallback((fn: NavigateFunction | null) => {
    navigateRef.current = fn;
  }, []);

  // Track which navigate events we've already auto-actioned so we don't
  // double-navigate on re-renders.
  const handledNavigateIdsRef = useRef<Set<string>>(new Set());
  const autoNavigateEnabledRef = useRef(Boolean(preferences.argusAutoNavigate));
  useEffect(() => {
    autoNavigateEnabledRef.current = Boolean(preferences.argusAutoNavigate);
  }, [preferences.argusAutoNavigate]);

  const handleEvent = useCallback(
    (event: ArgusGatewayEvent) => {
      dispatch({ type: "add", event });

      // Any agent-side event means the agent is no longer "just about to
      // start" — clear the ephemeral awaiting-response indicator. (The state
      // setter is stable so this doesn't need to be in the deps.)
      if (
        event.type === "turn_start" ||
        event.type === "message_start" ||
        event.type === "message_update" ||
        event.type === "tool_execution_start"
      ) {
        setIsAwaitingResponse(false);
      }

      // New turn starting — clear the "stopped" indicator.
      if (event.type === "turn_start") {
        setWasStopped(false);
      }

      // `agent_end` is the authoritative "pi's prompt() finished" signal —
      // one fires per session.prompt() call regardless of how many inner
      // turn_* iterations happened. Clear the optimistic flag set by
      // sendMessage, and invalidate any stale snapshot so the stop button
      // correctly hides once the agent is idle.
      if (event.type === "agent_end") {
        setAwaitingAgentEnd(false);
        setHydratedStreaming(false);
      }

      // Backend queue state update — pi emits this on steer/followUp/clearQueue.
      if (event.type === "queue_update") {
        const qe = event as {
          steering: readonly string[];
          followUp: readonly string[];
        };
        setQueuedMessages({ steering: qe.steering, followUp: qe.followUp });
      }

      // Phase 4b auto-navigate: on successful `navigate` tool completion,
      // when the user's auto-navigate preference is on, trigger an in-app
      // route change via the bridged navigate function. Skip on error.
      //
      // The tool's execute() returns `AgentToolResult` which is wrapped as
      // `{ content, details: { path, label, reason } }`, so we read the
      // payload from `event.result.details`.
      if (event.type === "tool_execution_end" && event.toolName === "navigate" && !event.isError) {
        if (handledNavigateIdsRef.current.has(event.toolCallId)) return;
        handledNavigateIdsRef.current.add(event.toolCallId);
        const resultPayload = event.result as
          | {
              details?: {
                path?: string;
                label?: string;
                reason?: string | null;
              };
            }
          | undefined;
        const details = resultPayload?.details;
        if (!details?.path) return;
        // On `/argus` the user is watching the full chat — auto-navigate
        // would pull focus. The message list renders a clickable card instead.
        // From the sidebar (any other page), auto-navigate only when the
        // user's `argusAutoNavigate` preference is on; otherwise the
        // sidebar's related-resources panel shows the link for manual click.
        const onArgusPage = pathnameRef.current.startsWith("/argus");
        if (!onArgusPage && autoNavigateEnabledRef.current && navigateRef.current) {
          try {
            const enrichedPath = appendArgusParams(details.path, {
              sessionId: activeSessionIdRef.current,
              instanceId: instanceIdRef.current,
            });
            navigateRef.current(enrichedPath);
            setSidebarOpen(true);
          } catch (err) {
            console.warn("[argus] auto-navigate failed:", err);
          }
        }
        // On `/argus`, the <ArgusNavigateCard /> renders a clickable
        // "Open" button — navigation is opt-in via that click.
      }
    },
    [setSidebarOpen],
  );

  // Refs so the memoised event handler can see the latest session +
  // instance IDs without re-creating (and re-subscribing SSE) on every
  // change.
  const activeSessionIdRef = useRef<string | null>(null);
  const instanceIdRef = useRef<string | undefined>(undefined);

  const handleAuthError = useCallback(() => {
    // 401/403 usually means the gateway token rotated — refetch the instance
    // detail to pick up the fresh access_token.
    void refetchInstance();
  }, [refetchInstance]);

  const { isConnected, error: connectionError } = useArgusSSE(
    hostUrl,
    activeSessionId,
    accessToken,
    { onEvent: handleEvent, onAuthError: handleAuthError },
  );

  // --- Status-drift auto-heal ---
  //
  // E2B auto-pauses sandboxes on idle timeout, but cope-api's view of the
  // instance can lag until the next health-check cron tick. In that window
  // the SSE stream opens against a paused host and errors with
  // `TypeError: Failed to fetch` — and the user has no recovery path because
  // the Resume button in <ArgusInstanceControls /> only shows for
  // `instance_status === "paused"`. Auto-fire POST /heal once per drift
  // episode to reconcile the DB state (which also auto-resumes the sandbox
  // via e2b's connect auto-resume semantics). A ref tracks the error string
  // we're currently healing against so we don't thrash if the mutation
  // itself fails and gets retried on the next render.
  const heal = useHealArgusInstance();
  // Stable ref so sendMessage can access heal without adding the mutation
  // (which changes identity on status transitions) to its dependency array.
  const healMutateRef = useRef(heal.mutateAsync);
  healMutateRef.current = heal.mutateAsync;
  const healTriggeredForErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (isConnected) {
      healTriggeredForErrorRef.current = null;
      return;
    }
    if (!connectionError) {
      healTriggeredForErrorRef.current = null;
      return;
    }
    if (!activeSessionId) return;
    if (!instanceId) return;
    if (instance?.instance_status !== "active") return;
    if (healTriggeredForErrorRef.current === connectionError) return;

    healTriggeredForErrorRef.current = connectionError;
    console.warn(
      `[argus] drift detected (error="${connectionError}") — auto-healing instance ${instanceId}`,
    );
    heal.mutate(
      { instanceId },
      {
        onError: (err) => {
          console.warn("[argus] auto-heal failed:", err);
          Sentry.captureException(err, {
            tags: { source: "argus-auto-heal" },
            extra: { instanceId, connectionError },
          });
        },
      },
    );
  }, [isConnected, connectionError, activeSessionId, instanceId, instance?.instance_status, heal]);

  // Transcript loading — delegates to cope-api which proxies the live
  // gateway when available and falls back to the S3 backup archive when
  // not. The hook's 4-retry exponential backoff replaces the previous
  // inline retry loop; the live-path resilience during gateway boot
  // races after /update or /resume is preserved because cope-api's
  // proxy has the same error surface as a direct gateway call.
  const {
    transcript: transcriptPayload,
    isLoading: isTranscriptFetchLoading,
    isFetching: isTranscriptFetching,
    error: transcriptError,
    refetch: refetchTranscript,
  } = useArgusSessionTranscript(activeSessionId);

  // Reset transcript state when the user switches sessions or clears
  // the active session. Independent of the data-fetch effect below so
  // the loading indicator stays responsive.
  useEffect(() => {
    if (!activeSessionId) {
      dispatch({ type: "reset" });
      setOptimisticUserMessages([]);
      setIsAwaitingResponse(false);
      setIsTranscriptLoading(false);
      setIsReadOnly(false);
      setQueuedMessages({ steering: [], followUp: [] });
      setWasStopped(false);
      setHydratedStreaming(false);
      setHydratedOpenToolCallIds([]);
      setAwaitingAgentEnd(false);
      return;
    }
    dispatch({ type: "reset" });
    setIsAwaitingResponse(false);
    setIsTranscriptLoading(true);
    setHydratedStreaming(false);
    setHydratedOpenToolCallIds([]);
  }, [activeSessionId]);

  // Seed the event buffer when the transcript payload arrives. React
  // Query handles the retry/backoff internally — we just react to the
  // resolved state.
  useEffect(() => {
    if (!activeSessionId) return;
    if (transcriptPayload) {
      dispatch({
        type: "seed",
        messages: transcriptPayload.messages as ArgusMessage[],
      });
      setIsReadOnly(Boolean(transcriptPayload.read_only));
      setOptimisticUserMessages([]);
      setIsTranscriptLoading(false);
      return;
    }
    if (transcriptError) {
      console.warn("[argus] failed to load transcript:", transcriptError);
      Sentry.captureException(transcriptError, {
        tags: { source: "argus-transcript-load" },
        extra: { activeSessionId },
      });
      setIsTranscriptLoading(false);
      setIsReadOnly(false);
      return;
    }
    // Still loading / fetching — keep the spinner.
    setIsTranscriptLoading(isTranscriptFetchLoading || isTranscriptFetching);
  }, [
    activeSessionId,
    transcriptPayload,
    transcriptError,
    isTranscriptFetchLoading,
    isTranscriptFetching,
  ]);

  // Queue state is fetched separately from the gateway because the
  // transcript route doesn't expose it. Skipped in read-only mode (the
  // sandbox is gone, there's no queue to read).
  //
  // The same call also returns live `streaming` + `openToolCallIds` so
  // the UI can hydrate the agent-working state and in-flight tool blocks
  // before the SSE stream catches up.
  const refreshQueueSnapshot = useCallback(() => {
    if (!activeSessionId) return;
    if (!hostUrl) return;
    if (instanceStatus !== "active") return;
    if (isReadOnly) return;
    void gateway
      .getQueue(activeSessionId)
      .then((queue) => {
        if (!queue) return;
        setQueuedMessages({
          steering: queue.steering,
          followUp: queue.followUp,
        });
        setHydratedStreaming(queue.streaming);
        setHydratedOpenToolCallIds(queue.openToolCallIds);
      })
      .catch(() => {
        // Best-effort — failing to load the queue doesn't break the
        // transcript view.
      });
  }, [activeSessionId, hostUrl, instanceStatus, isReadOnly, gateway]);

  useEffect(() => {
    refreshQueueSnapshot();
  }, [refreshQueueSnapshot]);

  // Resync on tab refocus. SSE pauses while the tab is hidden
  // (see use-argus-sse.ts `pauseWhenHidden`) and on reconnect only
  // streams new events from that moment forward — any events that
  // arrived during the hidden window are lost. When the user returns,
  // refetch the transcript (which re-seeds the event buffer via the
  // effect above) and re-fetch the queue snapshot so `isAgentWorking`
  // and open tool calls reflect current server state.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!activeSessionId) return;
      if (isReadOnly) return;
      void refetchTranscript();
      refreshQueueSnapshot();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [activeSessionId, isReadOnly, refetchTranscript, refreshQueueSnapshot]);

  // --- Gateway action wrappers ---

  // Keep a ref to the latest pathname so `sendMessage` — which is memoised —
  // always injects the *current* route context even after navigations.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);
  useEffect(() => {
    instanceIdRef.current = instanceId;
  }, [instanceId]);

  const sendMessage = useCallback(
    async (message: string, method: "steer" | "followUp" = "steer") => {
      const prefix = buildContextPrefix(pathnameRef.current);
      const finalMessage = prefix ? `${prefix}${message}` : message;

      // Show the user's own text immediately — pi-coding-agent only emits
      // assistant-side events, so without this the user's message wouldn't
      // appear until we reload the transcript on the next session switch.
      // Skip for followUp sends — those are queued by pi and shouldn't
      // appear as user messages until they're actually delivered.
      if (method !== "followUp") {
        setOptimisticUserMessages((prev) => [...prev, message]);
        // Flip the ephemeral "thinking" indicator on — handleEvent will clear
        // it as soon as the first turn_start / message_start / tool event
        // arrives for this turn.
        setIsAwaitingResponse(true);
        // Optimistically mark pi's prompt() as in-flight until the live
        // stream delivers a matching `agent_end`. Bridges the gap where
        // pi's initial `agent_start` fires before our SSE subscription
        // attaches (happens on first send of a new session).
        setAwaitingAgentEnd(true);
      }

      const attempt = async () => {
        const user = argusUserRef.current ?? undefined;
        if (!activeSessionId) {
          // Optimistically show a placeholder row in the session list while
          // the gateway bootstraps the real session.
          setPendingSessionPreview(message);
          try {
            const argusContext = buildDefaultArgusContext(pathnameRef.current);
            const result = await gateway.startSession(finalMessage, {
              argusContext,
              user,
            });
            setActiveSessionId(result.id);
          } finally {
            setPendingSessionPreview(null);
          }
          return;
        }
        await gateway.sendMessage(activeSessionId, finalMessage, method, {
          user,
        });
      };

      try {
        await attempt();
      } catch (err) {
        // Network error (sandbox unreachable — likely paused or gateway
        // crashed). Trigger /heal to resume + restart the gateway, then
        // retry once. This covers the "DB says active but sandbox is
        // actually paused" drift scenario.
        const isNetworkError = err instanceof TypeError && /failed to fetch/i.test(err.message);
        if (isNetworkError && instanceIdRef.current) {
          console.warn("[argus] send failed with network error — healing and retrying");
          try {
            await healMutateRef.current({ instanceId: instanceIdRef.current });
            await new Promise((r) => setTimeout(r, 2000));
            await attempt();
            return;
          } catch {
            // Heal or retry also failed — throw original error.
          }
        }
        throw err;
      }
    },
    [activeSessionId, gateway],
  );

  const startSession = useCallback(
    async (prompt: string): Promise<string> => {
      const prefix = buildContextPrefix(pathnameRef.current);
      const finalPrompt = prefix ? `${prefix}${prompt}` : prompt;
      setPendingSessionPreview(prompt);
      try {
        const argusContext = buildDefaultArgusContext(pathnameRef.current);
        const user = argusUserRef.current ?? undefined;
        const result = await gateway.startSession(finalPrompt, {
          argusContext,
          user,
        });
        setActiveSessionId(result.id);
        return result.id;
      } finally {
        setPendingSessionPreview(null);
      }
    },
    [gateway],
  );

  const stopSession = useCallback(async () => {
    if (!activeSessionId) return;
    setWasStopped(true);
    await gateway.stopSession(activeSessionId);
  }, [activeSessionId, gateway]);

  // --- Phase 4a: ask_user question tracking ---
  //
  // Derived from the event buffer so it stays in sync without a separate
  // reducer. A pending entry exists for every `tool_execution_start` with
  // `toolName === "ask_user"` that has not yet seen a matching
  // `tool_execution_end`.
  const pendingQuestions = useMemo<PendingQuestion[]>(() => {
    const endedToolCallIds = new Set<string>();
    events.forEach((event) => {
      if (event.type === "tool_execution_end") {
        endedToolCallIds.add(event.toolCallId);
      }
    });
    const results: PendingQuestion[] = [];
    events.forEach((event) => {
      if (event.type !== "tool_execution_start") return;
      if (event.toolName !== "ask_user") return;
      if (endedToolCallIds.has(event.toolCallId)) return;
      results.push({
        toolCallId: event.toolCallId,
        sessionId: activeSessionId ?? "",
        questions: (event.args as { questions?: unknown })?.questions,
        createdAt: Date.now(),
      });
    });
    return results;
  }, [events, activeSessionId]);

  const answerQuestion = useCallback(
    async (toolCallId: string, answers: unknown[]) => {
      if (!hostUrl) {
        throw new Error("[argus] gateway not available");
      }
      if (!activeSessionId) {
        throw new Error("[argus] no active session to answer a question for");
      }
      const url = `${hostUrl.replace(/\/+$/, "")}/session/cmds/answer/${encodeURIComponent(
        activeSessionId,
      )}/${encodeURIComponent(toolCallId)}`;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["e2b-traffic-access-token"] = accessToken;
      }
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          answers,
          user: argusUserRef.current ?? undefined,
        }),
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const message =
          (errorBody as { error?: string } | null)?.error ?? `HTTP error ${response.status}`;
        throw new Error(message);
      }
      // The matching `tool_execution_end` event will arrive via SSE — the UI
      // flips status on that event, so there's nothing more to do here.
    },
    [hostUrl, accessToken, activeSessionId],
  );

  const dismissQuestion = useCallback(
    async (toolCallId: string) => {
      // Dismiss sends a "user declined" answer so the agent can move on.
      await answerQuestion(toolCallId, [{ dismissed: true }]);
    },
    [answerQuestion],
  );

  // --- Phase 6: isAgentWorking + queue management ---

  const isAgentWorking = useMemo(() => {
    // `agent_start`/`agent_end` wrap the entire `session.prompt()` call —
    // exactly what `session.isStreaming` tracks on the backend. One pair
    // per prompt(), regardless of how many inner `turn_*` iterations pi
    // performs for tool use. Using turn_* here would flicker the button
    // off between each LLM call in a multi-tool response.
    let agentOpen = 0;
    let sawAgentStart = false;
    for (const e of events) {
      if (e.type === "agent_start") {
        agentOpen++;
        sawAgentStart = true;
      } else if (e.type === "agent_end") {
        agentOpen--;
      }
    }
    // Live-stream agent loop is open — authoritative.
    if (agentOpen > 0) return true;
    // Optimistic "I just sent, waiting for the live agent_end" — handles
    // the new-session race where pi's initial agent_start fires before
    // our SSE subscription attaches.
    if (awaitingAgentEnd) return true;
    // We observed a full agent cycle in the live stream — trust it over
    // any stale snapshot.
    if (sawAgentStart) return false;
    // Fall back to the one-shot snapshot from `GET /session/queue/:id`.
    // This catches the "refresh mid-turn" case: snapshot is taken while
    // pi is already streaming, so it's `true`.
    return hydratedStreaming;
  }, [events, hydratedStreaming, awaitingAgentEnd]);

  const clearQueue = useCallback(async () => {
    if (!activeSessionId) return;
    await gateway.clearQueue(activeSessionId);
  }, [activeSessionId, gateway]);

  const removeFromQueue = useCallback(
    async (type: "steering" | "followUp", index: number) => {
      if (!activeSessionId) return;
      await gateway.removeFromQueue(activeSessionId, type, index);
    },
    [activeSessionId, gateway],
  );

  // sendMessage is referenced by promoteToSteer below — we keep a stable
  // ref so the callback's identity doesn't churn on every send.
  const sendMessageRef = useRef<typeof sendMessage | null>(null);

  const promoteToSteer = useCallback(
    async (type: "steering" | "followUp", index: number, text: string) => {
      if (!activeSessionId) return;
      // Optimistically remove from the queue first so the row disappears
      // immediately; the queue_update SSE event will reconcile shortly after.
      try {
        await gateway.removeFromQueue(activeSessionId, type, index);
      } catch (err) {
        // If the remove fails (e.g. queue shifted under us), don't double-send.
        console.warn("[argus] promoteToSteer: removeFromQueue failed", err);
        throw err;
      }
      const send = sendMessageRef.current;
      if (!send) return;
      await send(text, "steer");
    },
    [activeSessionId, gateway],
  );

  // Keep the sendMessage ref in sync so promoteToSteer can call the
  // latest closure without taking a dependency on it.
  sendMessageRef.current = sendMessage;

  return {
    sidebarOpen,
    toggleSidebar,
    setSidebarOpen,
    activeSessionId,
    setActiveSessionId,
    pendingSessionPreview,
    optimisticUserMessages,
    isAwaitingResponse,
    isTranscriptLoading,
    isReadOnly,
    isAgentWorking,
    wasStopped,
    queuedMessages,
    clearQueue,
    removeFromQueue,
    promoteToSteer,
    instanceStatus,
    events,
    seededMessages,
    hydratedOpenToolCallIds,
    isConnected,
    connectionError,
    sendMessage,
    startSession,
    stopSession,
    pendingQuestions,
    answerQuestion,
    dismissQuestion,
    registerNavigate,
  };
};
